import {
  ArrowDownward,
  ArrowUpward,
  CalendarToday,
  Close,
  Description,
  ExpandLess,
  ExpandMore,
  Folder,
  FolderOpen,
  Search,
  SortByAlpha,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import { formatFileSize as formatBytes } from "src/helpers/formatFileSize";
import { DatasetArtifactStream } from "./components/DatasetArtifactStream";

/**
 * Artifact type from the backend
 */
export interface Artifact {
  id: number;
  filename: string;
  filetype: string;
  filesize: number | null; // null = loading, -1 = error, 0+ = actual size
  create_date: string;
  create_user_fk: number;
  sdr_id?: number;
  dataset_id?: number;
  dataset_name?: string;
}

/**
 * Dataset type for the selector
 */
export interface Dataset {
  id: number;
  name: string;
  [key: string]: any;
}

/**
 * Enriched artifact with dataset info
 */
export type EnrichedArtifact = Artifact & {
  dataset_id: number;
  dataset_name: string;
};

interface ArtifactSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedArtifacts: EnrichedArtifact[]) => void;
  datasets: Dataset[];
  defaultSelectedArtifacts?: EnrichedArtifact[];
}

const areSetsEqual = (a: Set<number>, b: Set<number>) => {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
};

/**
 * Format artifact filesize with explicit loading / unknown states.
 *
 * - ``null`` → "Loading..." (size not yet calculated)
 * - ``undefined`` / ``-1`` → "Unknown size" (error state)
 * - otherwise → delegates numeric formatting to the shared
 *   :func:`formatFileSize` helper ("0 B", "1.5 MB", etc.)
 */
const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null) return "Loading...";
  if (bytes === undefined || bytes === -1) return "Unknown size";
  return formatBytes(bytes);
};

/**
 * ArtifactSelectorModal - A modal dialog for selecting artifacts from multiple datasets
 * Displays artifacts grouped by dataset with search and multi-select capabilities
 */
const ArtifactSelectorModal = ({
  open,
  onClose,
  onConfirm,
  datasets,
  defaultSelectedArtifacts = [],
}: ArtifactSelectorModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "date">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [expandedDatasets, setExpandedDatasets] = useState<Set<number>>(
    new Set(),
  );
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<Set<number>>(
    new Set(),
  );
  const [artifactsByDatasetId, setArtifactsByDatasetId] = useState<
    Map<number, EnrichedArtifact[]>
  >(new Map());

  const normalizedSearchQuery = debouncedSearchQuery.trim().toLowerCase();

  // Debounce search query for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 150);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Callback for when dataset artifacts are updated via SSE stream
  const handleArtifactsUpdate = useCallback(
    (datasetId: number, artifacts: EnrichedArtifact[]) => {
      setArtifactsByDatasetId((prev) => {
        const newMap = new Map(prev);
        newMap.set(datasetId, artifacts);
        return newMap;
      });
    },
    [],
  );

  // Check if any dataset is still loading
  const isLoading = false; // All loading is managed by individual stream components

  // Build enriched artifacts map with dataset info
  const artifactsMap = useMemo(() => {
    const map = new Map<number, EnrichedArtifact>();
    artifactsByDatasetId.forEach((artifacts: EnrichedArtifact[]) => {
      artifacts.forEach((artifact: EnrichedArtifact) => {
        map.set(artifact.id, artifact);
      });
    });
    return map;
  }, [artifactsByDatasetId]);

  // Group artifacts by dataset for display
  const artifactsByDataset = useMemo(() => {
    const grouped: Map<number, EnrichedArtifact[]> = new Map();

    datasets.forEach((dataset) => {
      const artifacts = artifactsByDatasetId.get(dataset.id) || [];
      if (artifacts.length > 0) {
        // Sort artifacts
        const sortedArtifacts = [...artifacts].sort(
          (a: EnrichedArtifact, b: EnrichedArtifact) => {
            let compareValue = 0;
            if (sortBy === "name") {
              compareValue = cleanArtifactFilename(a.filename).localeCompare(
                cleanArtifactFilename(b.filename),
              );
            } else {
              const aDate = new Date(a.create_date || 0).getTime();
              const bDate = new Date(b.create_date || 0).getTime();
              compareValue = aDate - bDate;
            }
            return sortOrder === "asc" ? compareValue : -compareValue;
          },
        );

        grouped.set(dataset.id, sortedArtifacts);
      }
    });

    return grouped;
  }, [datasets, artifactsByDatasetId, sortBy, sortOrder]);

  // Initialize selection from defaults
  useEffect(() => {
    if (defaultSelectedArtifacts && defaultSelectedArtifacts.length > 0) {
      const selectedIds = new Set(
        defaultSelectedArtifacts.map((artifact) => artifact.id),
      );
      setSelectedArtifactIds(selectedIds);
    } else {
      setSelectedArtifactIds(new Set());
    }
  }, [defaultSelectedArtifacts, open]);

  // Auto-expand datasets when modal opens or when search matches
  useEffect(() => {
    if (!open) return;

    if (normalizedSearchQuery) {
      // Expand datasets that have matching artifacts
      const datasetsToExpand = new Set<number>();
      artifactsByDataset.forEach((artifacts, datasetId) => {
        const hasMatch = artifacts.some((artifact) =>
          cleanArtifactFilename(artifact.filename)
            .toLowerCase()
            .includes(normalizedSearchQuery),
        );
        if (hasMatch) {
          datasetsToExpand.add(datasetId);
        }
      });
      setExpandedDatasets((prev) =>
        areSetsEqual(prev, datasetsToExpand) ? prev : datasetsToExpand,
      );
    } else {
      // Expand all datasets by default when no search
      const allDatasetIds = new Set(datasets.map((d) => d.id));
      setExpandedDatasets((prev) =>
        areSetsEqual(prev, allDatasetIds) ? prev : allDatasetIds,
      );
    }
  }, [normalizedSearchQuery, artifactsByDataset, datasets, open]);

  // Get selected artifacts array
  const selectedArtifacts: EnrichedArtifact[] = useMemo(() => {
    return Array.from(selectedArtifactIds)
      .map((id) => artifactsMap.get(id))
      .filter((artifact): artifact is EnrichedArtifact => artifact != null);
  }, [selectedArtifactIds, artifactsMap]);

  // Total artifact count
  const totalArtifactCount = useMemo(() => {
    let count = 0;
    artifactsByDataset.forEach((artifacts) => {
      count += artifacts.length;
    });
    return count;
  }, [artifactsByDataset]);

  // Toggle dataset expand/collapse
  const toggleDataset = (datasetId: number) => {
    setExpandedDatasets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(datasetId)) {
        newSet.delete(datasetId);
      } else {
        newSet.add(datasetId);
      }
      return newSet;
    });
  };

  // Toggle artifact selection
  const toggleArtifactSelection = (artifactId: number) => {
    setSelectedArtifactIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(artifactId)) {
        newSet.delete(artifactId);
      } else {
        newSet.add(artifactId);
      }
      return newSet;
    });
  };

  // Select all artifacts in a dataset
  const selectAllInDataset = (datasetId: number) => {
    const artifacts = artifactsByDataset.get(datasetId) || [];
    setSelectedArtifactIds((prev) => {
      const newSet = new Set(prev);
      artifacts.forEach((artifact) => newSet.add(artifact.id));
      return newSet;
    });
  };

  // Deselect all artifacts in a dataset
  const deselectAllInDataset = (datasetId: number) => {
    const artifacts = artifactsByDataset.get(datasetId) || [];
    const artifactIds = new Set(artifacts.map((a) => a.id));
    setSelectedArtifactIds((prev) => {
      const newSet = new Set(prev);
      artifactIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  };

  // Check if all artifacts in a dataset are selected
  const areAllInDatasetSelected = (datasetId: number) => {
    const artifacts = artifactsByDataset.get(datasetId) || [];
    if (artifacts.length === 0) return false;
    return artifacts.every((artifact) => selectedArtifactIds.has(artifact.id));
  };

  // Check if some (but not all) artifacts in a dataset are selected
  const areSomeInDatasetSelected = (datasetId: number) => {
    const artifacts = artifactsByDataset.get(datasetId) || [];
    if (artifacts.length === 0) return false;
    const selectedCount = artifacts.filter((artifact) =>
      selectedArtifactIds.has(artifact.id),
    ).length;
    return selectedCount > 0 && selectedCount < artifacts.length;
  };

  const handleConfirm = () => {
    onConfirm(selectedArtifacts);
    onClose();
  };

  const handleCancel = () => {
    // Reset to defaults
    if (defaultSelectedArtifacts && defaultSelectedArtifacts.length > 0) {
      const selectedIds = new Set(
        defaultSelectedArtifacts.map((artifact) => artifact.id),
      );
      setSelectedArtifactIds(selectedIds);
    } else {
      setSelectedArtifactIds(new Set());
    }
    onClose();
  };

  const handleClearSelection = () => {
    setSelectedArtifactIds(new Set());
  };

  const handleSelectAll = () => {
    const allArtifactIds = new Set<number>();
    artifactsByDataset.forEach((artifacts) => {
      artifacts.forEach((artifact) => allArtifactIds.add(artifact.id));
    });
    setSelectedArtifactIds(allArtifactIds);
  };

  return (
    <>
      {/* Render stream managers for each dataset - these handle SSE connections */}
      {datasets.map((dataset) => (
        <DatasetArtifactStream
          key={`stream-${dataset.id}`}
          dataset={dataset}
          searchQuery={debouncedSearchQuery}
          enabled={open && datasets.length > 0}
          onArtifactsUpdate={handleArtifactsUpdate}
        />
      ))}

      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        data-testid="artifact-selector-modal"
        PaperProps={{
          sx: {
            height: isMobile ? "100vh" : "80vh",
            maxHeight: isMobile ? "100vh" : "800px",
          },
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold">
              Select Files from Datasets
            </Typography>
            <IconButton
              onClick={handleCancel}
              size="small"
              data-attr="assistant:btn:close-artifact-selector"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {/* Search and Action Bar */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 1 : 2}
            alignItems="stretch"
            sx={{ p: 2, pb: 1 }}
          >
            <TextField
              placeholder="Search files by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              fullWidth
              data-testid="artifact-selector-search"
              data-attr="assistant:input:search-artifacts"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={handleClearSelection}
                disabled={selectedArtifacts.length === 0}
                fullWidth={isMobile}
                data-attr="assistant:btn:clear-artifact-selection"
              >
                Clear
              </Button>
              <Button
                size="small"
                onClick={handleSelectAll}
                disabled={totalArtifactCount === 0}
                fullWidth={isMobile}
                data-attr="assistant:btn:select-all-artifacts"
              >
                Select All
              </Button>
            </Stack>
          </Stack>

          {/* Sort Controls */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 2, pb: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Sort by:
            </Typography>
            <Button
              size="small"
              variant={sortBy === "name" ? "contained" : "outlined"}
              onClick={() => setSortBy("name")}
              startIcon={<SortByAlpha fontSize="small" />}
              sx={{ textTransform: "none" }}
              data-attr="assistant:btn:sort-artifacts-by-name"
            >
              Name
            </Button>
            <Button
              size="small"
              variant={sortBy === "date" ? "contained" : "outlined"}
              onClick={() => setSortBy("date")}
              startIcon={<CalendarToday fontSize="small" />}
              sx={{ textTransform: "none" }}
              data-attr="assistant:btn:sort-artifacts-by-date"
            >
              Date
            </Button>
            <IconButton
              size="small"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              data-attr="assistant:btn:toggle-artifact-sort-order"
            >
              <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
                {sortOrder === "asc" ? (
                  <ArrowUpward fontSize="small" />
                ) : (
                  <ArrowDownward fontSize="small" />
                )}
              </Tooltip>
            </IconButton>
          </Stack>

          {/* Selection Summary */}
          <Box sx={{ px: 2, pb: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              {selectedArtifacts.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedArtifacts.length} file
                  {selectedArtifacts.length !== 1 ? "s" : ""}
                </Typography>
              )}
              {debouncedSearchQuery && (
                <Typography variant="body2" color="text.secondary">
                  Showing: {totalArtifactCount} result
                  {totalArtifactCount !== 1 ? "s" : ""}
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Artifacts Tree View */}
          <Box
            sx={{
              height: "calc(100% - 180px)",
              px: 2,
              overflowY: "auto",
            }}
          >
            {datasets.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Folder sx={{ fontSize: 64, color: "text.disabled" }} />
                <Typography variant="body1" color="text.secondary">
                  No datasets selected
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Please select datasets first to browse their files.
                </Typography>
              </Box>
            ) : isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography>Loading files...</Typography>
              </Box>
            ) : (
              <List
                sx={{ width: "100%", bgcolor: "background.paper" }}
                data-testid="artifact-selector-list"
              >
                {datasets.map((dataset) => {
                  const artifacts = artifactsByDataset.get(dataset.id) || [];
                  const isExpanded = expandedDatasets.has(dataset.id);
                  const allSelected = areAllInDatasetSelected(dataset.id);
                  const someSelected = areSomeInDatasetSelected(dataset.id);

                  return (
                    <Box
                      key={`dataset-${dataset.id}`}
                      data-testid={`artifact-selector-dataset-${dataset.id}`}
                    >
                      <ListItem
                        disablePadding
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <ListItemButton
                          onClick={() => toggleDataset(dataset.id)}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={allSelected}
                              indeterminate={someSelected}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (allSelected) {
                                  deselectAllInDataset(dataset.id);
                                } else {
                                  selectAllInDataset(dataset.id);
                                }
                              }}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemIcon>
                            {isExpanded ? (
                              <FolderOpen color="primary" />
                            ) : (
                              <Folder color="primary" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={dataset.name}
                            secondary={`${artifacts.length} file${
                              artifacts.length !== 1 ? "s" : ""
                            }`}
                            primaryTypographyProps={{
                              fontWeight: "bold",
                            }}
                          />
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>

                      {/* Artifacts in dataset */}
                      {isExpanded &&
                        artifacts.map((artifact) => (
                          <ListItem
                            key={`artifact-${artifact.id}`}
                            disablePadding
                            data-testid={`artifact-selector-item-${artifact.id}`}
                            sx={{
                              pl: 4,
                              borderBottom: 1,
                              borderColor: "divider",
                              bgcolor: theme.palette.action.hover,
                            }}
                          >
                            <ListItemButton
                              onClick={() =>
                                toggleArtifactSelection(artifact.id)
                              }
                              dense
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={selectedArtifactIds.has(artifact.id)}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemIcon>
                                <Description color="action" />
                              </ListItemIcon>
                              <ListItemText
                                primary={cleanArtifactFilename(
                                  artifact.filename,
                                )}
                                secondary={formatFileSize(artifact.filesize)}
                                primaryTypographyProps={{
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}

                      {/* No artifacts message */}
                      {isExpanded && artifacts.length === 0 && (
                        <ListItem
                          sx={{
                            pl: 4,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: theme.palette.action.hover,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontStyle="italic"
                              >
                                No files found
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={handleCancel}
            color="inherit"
            fullWidth={isMobile}
            data-testid="artifact-selector-cancel"
            data-attr="assistant:btn:cancel-artifact-selection"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            data-testid="artifact-selector-confirm"
            data-attr="assistant:btn:confirm-artifact-selection"
          >
            OK ({selectedArtifacts.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ArtifactSelectorModal;
